import { AxiosResponse } from "axios";
import qs from "query-string";
import HttpService from "src/services/httpService";
import RouteService from "src/services/routeService";
import { ApiResponse, PaginatedResponse } from "src/types";
import { downloadFromUrl } from "src/ui/utils/download";

import { unpackError } from "@dashboard/devices/api/helpers";

const getPresignedDownloadUrl = async (fileId: string) => {
    try {
        const endpoint = await RouteService.getStorageRoute();
        const response: AxiosResponse<{ model: string }> = await HttpService.get(`${endpoint}files/${fileId}/link`);
        return response.data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

const invokeFileDownload = async (id: string | number, fileName: string, productFile?: boolean) => {
    try {
        const productEndpoint = await RouteService.getProductRoute();
        const storageEndpoint = await RouteService.getStorageRoute();
        const response: AxiosResponse<ApiResponse<string>> = await HttpService.get(
            `${productFile ? productEndpoint : storageEndpoint}files/${id}/link`,
        );

        downloadFromUrl(response.data.model, fileName);
    } catch (error) {
        throw unpackError(error);
    }
};

export type GetPresignedUrlPayload = { workspaceId: number; groupId: number | null; filename: string };
type GetPresignedUrlResponse = { uploadPresignedUrl: string; fileId: string };

async function getPresignedUrl(payload: GetPresignedUrlPayload) {
    const endpoint = await RouteService.getStorageRoute();
    return await HttpService.post<GetPresignedUrlPayload, AxiosResponse<ApiResponse<GetPresignedUrlResponse>>>(
        endpoint + "files",
        payload,
    );
}

type GetUploadStatusPayload = GetPresignedUrlPayload & { fileId: string; pageSize: number };

async function getUploadStatus(payload: GetUploadStatusPayload) {
    const endpoint = await RouteService.getStorageRoute();
    const query = qs.stringify(
        {
            workspaceId: payload.workspaceId,
            groups: payload.groupId ? [payload.groupId] : undefined,
            filename: payload.filename,
            // 0 includes uploads that are not finished yet; by default the list leaves them out.
            uploadStatus: 0,
            pageSize: payload.pageSize,
            pageIndex: 1,
        },
        { arrayFormat: "index" },
    );
    const response = await HttpService.get<unknown, AxiosResponse<ApiResponse<PaginatedResponse<API.File>>>>(
        `${endpoint}files?${query}`,
    );
    return response.data.model.list.find((file) => file.id === payload.fileId)?.status;
}

export default {
    getPresignedDownloadUrl,
    getPresignedUrl,
    getUploadStatus,
    invokeFileDownload,
};
